import React, {useEffect, useState} from 'react';
import {
  TextField,
  Typography,
  Box,
  Grid,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
} from '@mui/material';
import DataTable from 'examples/Tables/DataTable';
import MDBox from 'components/MDBox';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import ComplexStatisticsCard from 'examples/Cards/StatisticsCards/ComplexStatisticsCard';
import MDButton from 'components/MDButton';
import ReportsBarChart from 'examples/Charts/BarCharts/ReportsBarChart';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import {Link} from 'react-router-dom';
import {useParams} from 'react-router-dom';
import {Axios} from '../../services/axios';
import {Block} from 'notiflix';
import MDSnackbar from 'components/MDSnackbar';
import {useNavigate} from 'react-router-dom';
import {useForm} from 'react-hook-form';

const columns = [
  {Header: 'Serial', accessor: 'sr', width: '5%', align: 'left'},
  {Header: 'Slider Name', accessor: 'name', width: '45%', align: 'left'},
  {Header: 'position', accessor: 'position', align: 'left'},
  {Header: 'status', accessor: 'status', align: 'center'},
  {Header: 'action', accessor: 'action', align: 'center'},
];

const getRow = (Slider, idx, pageId) => ({
  sr: <MDTypography variant="caption">{idx + 1}</MDTypography>,
  name: (
    <MDTypography display="block" variant="button" fontWeight="medium">
      {Slider.name}
    </MDTypography>
  ),
  position: <MDTypography variant="caption">{Slider.position}</MDTypography>,
  status: (
    <MDBox ml={-1}>
      <MDBadge
        badgeContent={Slider.status ? 'active' : 'inactive'}
        color="success"
        variant="gradient"
        size="sm"
      />
    </MDBox>
  ),
  action: (
    <MDBox ml={-1}>
      <Link to={`/pages/edit/${pageId}/slider/${Slider.id}`}>
        <MDBadge
          badgeContent="edit"
          color="info"
          variant="gradient"
          size="sm"
        />
      </Link>
    </MDBox>
  ),
});

const DYNAMIC_ENTITY_CONFIGS = [
  {
    id: 'product',
    name: 'Product',
    route: '/api/products',
    description: 'For product detail pages',
  },
  {
    id: 'valuepack',
    name: 'ValuePack',
    route: '/api/valuepacks',
    description: 'For valuepack detail pages',
  },
  {
    id: 'category',
    name: 'Category',
    route: '/api/categories',
    description: 'For category detail pages',
  },
];

function Dashboard() {
  const {pageId} = useParams();
  const navigate = useNavigate();
  const [sliders, setSliders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessSB, setShowSuccessSB] = useState(false);
  const [pageData, setPageData] = useState({
    name: '',
    isActive: true,
    route: '',
    pageType: 'STATIC',
    dynamicEntities: [],
  });
  const [selectedDynamicEntities, setSelectedDynamicEntities] = useState([]);
  const [metrics, setMetrics] = useState({
    peakTrafficHour: {},
    todaysVisits: {},
    totalVisits: {},
    uniqueVisitors: {},
  });

  const [graphData, setGraphData] = useState({
    labels: [],
    datasets: {
      label: 'Visits',
      data: [],
    },
  });

  const {
    register,
    handleSubmit,
    formState: {errors},
    reset,
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      keywords: '',
      author: '',
      robots: '',
    },
  });

  useEffect(() => {
    fetchPageData();
  }, []);

  useEffect(() => {
    if (isLoading) Block.circle('.container', 'Loading Page Settings...');
    else Block.remove('.container');
  }, [isLoading]);

  useEffect(() => {
    if (showSuccessSB) {
      setTimeout(() => {
        closeSuccessSB();
      }, 3000);
    }
  }, [showSuccessSB]);

  useEffect(() => {
    if (pageData.route && pageData.route.includes(':')) {
      setPageData(prev => ({
        ...prev,
        pageType: 'DYNAMIC',
      }));
    } else {
      setPageData(prev => ({
        ...prev,
        pageType: 'STATIC',
      }));
      // Clear dynamic entities when switching to static
      setSelectedDynamicEntities([]);
    }
  }, [pageData.route]);

  const closeSuccessSB = () => setShowSuccessSB(false);

  const fetchPageData = async () => {
    setIsLoading(true);
    Axios.fetch('page/' + pageId)
      .then(res => {
        const {page, Metrics, graphData} = res.data?.data;

        console.log('response : ', res);

        setPageData({
          name: page?.name || '',
          isActive: page?.active,
          route: page?.route || '',
          pageType: page?.pageType || 'STATIC',
          dynamicEntities: page?.dynamicEntities || [],
        });

        setSelectedDynamicEntities(page?.dynamicEntities || []);

        setMetrics(Metrics);
        setGraphData(graphData);

        const seoData = {
          title: page?.PageSeo?.title || '',
          description: page?.PageSeo?.description || '',
          keywords: page?.PageSeo?.keywords || '',
          author: page?.PageSeo?.author || '',
          robots: page?.PageSeo?.robots || '',
        };
        reset(seoData);

        setSliders(page?.Sliders || []);
        setIsLoading(false);
      })
      .catch(err => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const handlePageSettingsChange = e => {
    const {name, value, checked, type} = e.target;
    setPageData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDynamicEntityChange = event => {
    const selectedIds = event.target.value;
    const selectedEntities = DYNAMIC_ENTITY_CONFIGS.filter(entity =>
      selectedIds.includes(entity.id),
    );
    setSelectedDynamicEntities(selectedEntities);
  };

  const handlePageSettingsSubmit = () => {
    //showing loader
    Block.circle('.page-settings-form', 'Saving Page Settings...');

    // Prepare payload for page settings
    const pageSettingsPayload = {
      name: pageData.name,
      isActive: pageData.isActive,
      route: pageData.route,
      pageType: pageData.pageType,
      dynamicEntities:
        pageData.pageType === 'DYNAMIC' ? selectedDynamicEntities : [],
    };

    //submitting form
    Axios.put('page/' + pageId, {pageSettings: pageSettingsPayload})
      .then(res => {
        //hiding loader
        Block.remove('.page-settings-form');
        //showing success message
        setShowSuccessSB(true);
        // Refresh page data to get updated values
        fetchPageData();
      })
      .catch(err => {
        console.log(err);
        //hiding loader
        Block.remove('.page-settings-form');
        alert(err.response?.data?.message || 'Error updating page settings');
      });
  };

  const onSubmitSeo = data => {
    //showing loader
    Block.circle('.seo-form', 'Saving SEO Settings...');
    //submitting form
    Axios.put('page/' + pageId, {seoSettigns: data})
      .then(res => {
        //hiding loader
        Block.remove('.seo-form');
        //showing success message
        setShowSuccessSB(true);
      })
      .catch(err => {
        console.log(err);
        //hiding loader
        Block.remove('.seo-form');
      });
  };

  const routeExamples = {
    STATIC: ['/home', '/about', '/contact'],
    DYNAMIC: ['/product/:id', '/user/:userId', '/category/:slug'],
  };

  return (
    <div className="container">
      <DashboardLayout>
        <DashboardNavbar routes={[{link: 'pages', name: 'Pages'}]} />
        <MDBox py={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={4}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      color="info"
                      icon="leaderboard"
                      title="Today's Visits"
                      count={metrics?.todaysVisits?.count}
                      percentage={{
                        color: 'success',
                        amount: metrics?.todaysVisits?.change?.count,
                        label:
                          'than ' + metrics?.todaysVisits?.change?.comparedTo,
                      }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      color="secondary"
                      icon="leaderboard"
                      title="Total Visits"
                      count={metrics?.totalVisits?.count}
                      percentage={{
                        color: 'success',
                        amount: metrics?.totalVisits?.change?.count,
                        label:
                          'than ' + metrics?.totalVisits?.change?.comparedTo,
                      }}
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      icon="leaderboard"
                      color="warning"
                      title="Unique Visits"
                      count={metrics?.uniqueVisitors?.count}
                      percentage={{
                        color: 'success',
                        amount: metrics?.uniqueVisitors?.change?.count,
                        label:
                          'than ' + metrics?.uniqueVisitors?.change?.comparedTo,
                      }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      icon="leaderboard"
                      color="primary"
                      title="Peak Hour"
                      count={metrics?.peakTrafficHour?.count}
                      percentage={{
                        color: 'success',
                        amount: metrics?.peakTrafficHour?.change?.count,
                        label:
                          'than ' +
                          metrics?.peakTrafficHour?.change?.comparedTo,
                      }}
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              <MDBox mb={1.5}>
                <ReportsBarChart
                  color="info"
                  title="Page Views"
                  description="Visits over the past 7 days"
                  date="Data updated: Just Now"
                  chart={graphData}
                />
              </MDBox>
            </Grid>
          </Grid>
          <MDBox mt={4.5}>
            <Grid container spacing={3}></Grid>
          </MDBox>
        </MDBox>

        {/* Page Settings Form */}
        <form className="page-settings-form">
          <Box mb={4}>
            <Typography variant="h5" gutterBottom>
              Page Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Page Name"
                  variant="outlined"
                  fullWidth
                  name="name"
                  value={pageData.name}
                  onChange={handlePageSettingsChange}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Route"
                  variant="outlined"
                  fullWidth
                  name="route"
                  value={pageData.route}
                  onChange={handlePageSettingsChange}
                  margin="normal"
                  helperText={`Examples: ${routeExamples[
                    pageData.pageType
                  ].join(', ')}`}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Page Type</InputLabel>
                  <Select
                    name="pageType"
                    value={pageData.pageType}
                    onChange={handlePageSettingsChange}
                    label="Page Type">
                    <MenuItem value="STATIC">Static Route</MenuItem>
                    <MenuItem value="DYNAMIC">Dynamic Route</MenuItem>
                  </Select>
                  <FormHelperText>
                    {pageData.pageType === 'STATIC'
                      ? 'Fixed URLs like /home, /about'
                      : 'URLs with parameters like /product/:id'}
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Dynamic Entities Configuration - Only show for dynamic pages */}
              {pageData.pageType === 'DYNAMIC' && (
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Dynamic Entities *</InputLabel>
                    <Select
                      multiple
                      value={selectedDynamicEntities.map(entity => entity.id)}
                      onChange={handleDynamicEntityChange}
                      renderValue={selected => (
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                          {selected.map(value => {
                            const entity = DYNAMIC_ENTITY_CONFIGS.find(
                              e => e.id === value,
                            );
                            return (
                              <Chip
                                key={value}
                                label={entity?.name}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            );
                          })}
                        </Box>
                      )}
                      label="Dynamic Entities *">
                      {DYNAMIC_ENTITY_CONFIGS.map(entity => (
                        <MenuItem key={entity.id} value={entity.id}>
                          <Box>
                            <Typography variant="body1">
                              {entity.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {entity.description} - API: {entity.route}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select the entity types this dynamic page will support for
                      FAQ assignments
                    </FormHelperText>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={pageData.isActive}
                      onChange={handlePageSettingsChange}
                      name="isActive"
                      color="primary"
                    />
                  }
                  label="Page Active Status"
                />
              </Grid>
            </Grid>
            <MDButton
              style={{marginTop: '16px'}}
              variant="gradient"
              color="info"
              onClick={handlePageSettingsSubmit}>
              Save Page Settings
            </MDButton>
          </Box>
        </form>

        {/* SEO Form */}
        <form className="seo-form" onSubmit={handleSubmit(onSubmitSeo)}>
          <Box mb={4}>
            <Typography variant="h5" gutterBottom>
              SEO Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Title"
                  variant="outlined"
                  fullWidth
                  {...register('title', {required: 'Title is required'})}
                  error={!!errors.title}
                  helperText={errors.title ? errors.title.message : ''}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Author"
                  variant="outlined"
                  fullWidth
                  {...register('author')}
                  error={!!errors.author}
                  helperText={errors.author ? errors.author.message : ''}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Description"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  {...register('description')}
                  error={!!errors.description}
                  helperText={
                    errors.description ? errors.description.message : ''
                  }
                  margin="normal"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Keywords"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  {...register('keywords')}
                  error={!!errors.keywords}
                  helperText={errors.keywords ? errors.keywords.message : ''}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Robots"
                  variant="outlined"
                  fullWidth
                  {...register('robots')}
                  error={!!errors.robots}
                  helperText={errors.robots ? errors.robots.message : ''}
                  margin="normal"
                />
              </Grid>
            </Grid>
            <MDButton
              style={{marginTop: '16px'}}
              variant="gradient"
              color="info"
              type="submit">
              Save SEO Settings
            </MDButton>
          </Box>
        </form>

        {/* Slider Section */}
        <Box mb={4}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              alignItems: 'baseline',
            }}>
            <Typography variant="h5" gutterBottom>
              Slider Management
            </Typography>
            <MDButton
              style={{marginTop: '16px'}}
              variant="gradient"
              color="info"
              onClick={() => navigate('slider')}>
              Add Slider +
            </MDButton>
          </div>
          <MDBox pt={3}>
            <DataTable
              table={{
                columns,
                rows: sliders?.map((Slider, idx) =>
                  getRow(Slider, idx, pageId),
                ),
              }}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              noEndBorder
            />
          </MDBox>
        </Box>

        <MDSnackbar
          color="success"
          icon="check"
          title="Success"
          content="Settings Saved Successfully"
          open={showSuccessSB}
          onClose={closeSuccessSB}
          close={closeSuccessSB}
          bgWhite
        />
        <Footer />
      </DashboardLayout>
    </div>
  );
}

export default Dashboard;
